<template>
  <div class="bill-list-container">
    <PremiumPage
      title="Bills"
      subtitle="View and manage individual house bills"
    >
      <div class="pa-6">
        <!-- ── Stats Row ─────────────────────────── -->
        <div class="stats-grid mb-6">
          <PremiumStatCard
            label="Total Billed"
            :value="formatCurrency(summary.total_billed)"
            icon="mdi-receipt-text-outline"
            color="#3B82F6"
            :loading="loading"
          />
          <PremiumStatCard
            label="Collected"
            :value="formatCurrency(summary.collected)"
            icon="mdi-check-circle-outline"
            color="#10B981"
            :loading="loading"
          />
          <PremiumStatCard
            label="Pending"
            :value="formatCurrency(summary.pending)"
            icon="mdi-clock-outline"
            color="#F59E0B"
            :loading="loading"
          />
          <PremiumStatCard
            label="Overdue"
            :value="formatCurrency(summary.overdue)"
            icon="mdi-alert-circle-outline"
            color="#EF4444"
            :loading="loading"
          />
        </div>

        <!-- ── Premium Table ─────────────────────── -->
        <PremiumTable
          title="Member Bills"
          :columns="columns"
          :items="bills"
          :loading="loading"
          :total-items="totalRecords"
          :page.sync="page"
          :items-per-page.sync="itemsPerPage"
          :searchable="true"
          @search="onSearch"
        >
          <!-- Header Extra: Date Filter + Export -->
          <template #header-extra>
            <div class="header-controls">
              <!-- Month Picker -->
              <PremiumDatePicker
                v-model="date"
                type="month"
                placeholder="Select Month"
                @change="getBills"
              />

              <!-- Export Button -->
              <button
                class="export-btn"
                :style="{ background: brandColor }"
                title="Export to Excel"
                @click="exportDialog = true"
              >
                <v-icon size="16" color="white">mdi-download-outline</v-icon>
              </button>
            </div>
          </template>

          <!-- Custom Body Columns -->
          <template #col-member_name="{ item }">
            <div class="member-cell">
              <span class="member-name">{{ item.member_name }}</span>
            </div>
          </template>

          <template #col-flat_house_number="{ item }">
            <div class="house-number-badge">
              <v-icon size="14" color="#94A3B8" v-if="item.unit_type === 1">
                mdi-home-outline
              </v-icon>
              <v-icon size="14" color="#94A3B8" v-else-if="item.unit_type === 2">
                mdi-store-outline
              </v-icon>
              <span v-if="item.member_flat" class="flat-prefix"
                >{{ item.member_flat }}-</span
              >
              <span class="house-num">{{ item.member_house }}</span>
            </div>
          </template>

          <template #col-title="{ item }">
            <span class="bill-title-text">{{ item.title }}</span>
          </template>

          <template #col-amount="{ item }">
            <span class="amount-text"
              >₹{{ Number(item.amount).toLocaleString() }}</span
            >
          </template>

          <template #col-created_at="{ item }">
            <span class="date-text">{{
              item.created_at | formatDateTime
            }}</span>
          </template>

          <template #col-is_paid="{ item }">
            <div class="status-pill-wrap">
              <div
                class="status-pill-btn"
                :class="item.is_paid == 0 ? 'status-pending' : 'status-completed'"
                @click.stop="toggleStatusMenu($event, item)"
              >
                <span class="dot"></span>
                {{ item.is_paid == "0" ? "Pending" : "Completed" }}
                <v-icon size="12" :color="item.is_paid == 0 ? '#f59e0b' : '#10b981'" class="ml-1">mdi-chevron-down</v-icon>
              </div>
            </div>
          </template>

          <template #col-paid_date="{ item }">
            <div class="paid-date-cell">
              <span class="date-text" v-if="item.is_paid != 0">{{
                item.paid_date | formatDateTime
              }}</span>
              <span v-else class="dash">—</span>

              <button
                v-if="item.bill_document"
                class="doc-view-btn"
                title="View Document"
                @click="openDocument(item.bill_document)"
              >
                <v-icon size="14">mdi-file-eye-outline</v-icon>
              </button>

               <!-- <button
                v-if="item.receipt_path && item.is_paid != 0"
                class="doc-view-btn"
                title="Download Receipt"
                style="margin-left: 4px; color: #3B82F6; "
                @click="downloadReceipt(item.receipt_path)"
              >
               <v-icon color="#10b981" size="20" >mdi-tray-arrow-down</v-icon>
              </button>  -->
            </div>
          </template>
        </PremiumTable>



        <!-- ── Export Confirmation ──────────────── -->
        <ConfirmModal
          v-model="exportDialog"
          type="primary"
          :btnColor="brandColor"
          title="Export to Excel"
          message="Are you sure you want to export the member bills to an Excel file?"
          confirm-text="Yes, Export"
          @confirm="
            exportToExcel();
            exportDialog = false;
          "
        />

        <!-- ── Payment Details Modal ──────────────── -->
        <PremiumModal
          v-model="paymentModal.open"
          title="Enter Payment Details"
          submit-text="Confirm Payment"
          cancel-text="Cancel"
          :max-width="440"
          :persistent="true"
          @submit="submitPaymentModal"
          @close="paymentModal.open = false"
        >
          <div style="margin-bottom: 16px;">
            <label style="font-size: 12px; font-weight: 600; color: #6b7280; display: block; margin-bottom: 6px;">Reference ID</label>
            <v-text-field
              v-model="paymentModal.ref_id"
              placeholder="Enter transaction / reference ID"
              outlined
              dense
              hide-details
            />
          </div>
          <div style="margin-bottom: 4px;">
            <label style="font-size: 12px; font-weight: 600; color: #6b7280; display: block; margin-bottom: 6px;">
              Payment Method <span style="color:#ef4444">*</span>
            </label>
            <v-select
              v-model="paymentModal.payment_method"
              :items="paymentMethods"
              placeholder="Select payment method"
              outlined
              dense
              :hide-details="!paymentErrors.payment_method"
              :error-messages="paymentErrors.payment_method"
            />
          </div>
          <div style="margin-bottom: 4px; margin-top: 12px;">
            <label style="font-size: 12px; font-weight: 600; color: #6b7280; display: block; margin-bottom: 6px;">
              Remark <span style="color:#ef4444">*</span>
            </label>
            <v-text-field
              v-model="paymentModal.remarks"
              placeholder="Enter remark"
              outlined
              dense
              :hide-details="!paymentErrors.remarks"
              :error-messages="paymentErrors.remarks"
            />
          </div>
          <div style="margin-top: 16px;">
            <PremiumDatePicker
              v-model="paymentModal.paid_date"
              label="Paid Date"
              placeholder="Select Date"
              :max="today"
              :clearable="false"
            />
          </div>
         

        </PremiumModal>

        <!-- ── Status Floating Dropdown ── -->
        <div
          v-if="statusMenu.open"
          class="status-dropdown-portal"
          :style="{ top: statusMenu.y + 'px', left: statusMenu.x + 'px' }"
          @click.stop
        >
          <div
            v-for="s in statusOptions"
            :key="s.value"
            class="status-option"
            :class="{ active: statusMenu.item && statusMenu.item.is_paid == s.value }"
            @click="selectStatus(statusMenu.item, s.value)"
          >
            <span class="status-dot" :style="{ background: s.value == 0 ? '#F59E0B' : '#10B981' }"></span>
            <span>{{ s.text }}</span>
            <v-icon v-if="statusMenu.item && statusMenu.item.is_paid == s.value" size="12" color="var(--brand)" class="ml-auto">mdi-check</v-icon>
          </div>
        </div>

        <!-- ── Media Viewer ── -->
        <MediaViewer
          v-model="imageDialog"
          :items="mediaItems_bill"
          :index="mediaIndex_bill"
        />
      </div>
    </PremiumPage>
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import moment from "moment";
import PremiumPage from "../../common/PremiumPage.vue";
import PremiumTable from "../../common/PremiumTable.vue";
import PremiumDatePicker from "../../common/PremiumDatePicker.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import PremiumStatCard from "../../common/PremiumStatCard.vue";
import MediaViewer from "../../common/MediaViewer.vue";

export default {
  name: "BillHouseList",

  components: {
    PremiumPage,
    PremiumTable,
    PremiumDatePicker,
    ConfirmModal,
    PremiumStatCard,
    MediaViewer,
  },

  data() {
    return {
      bills: [],
      summary: {
        total_billed: 0,
        collected: 0,
        pending: 0,
        overdue: 0,
      },
      loading: false,
      billStatusDialog: false,
      houseBill_details: {},
      status: 0,
      search: "",
      page: 1,
      itemsPerPage: 10,
      totalRecords: 0,
      menu: false,
      date: null,
      user: {},
      exportDialog: false,
      documents: [],
      base_url: process.env.VUE_APP_API_URL,
      statusMenu: { open: false, x: 0, y: 0, item: null },
      statusOptions: [
        { text: "Pending", value: 0 },
        { text: "Completed", value: 1 },
      ],
      imageDialog: false,
      mediaIndex_bill: 0,
      mediaItems_bill: [],
      paymentModal: { open: false, item: null, ref_id: '', payment_method: '', paid_date: null ,remarks: ''},
      paymentMethods: ['Cash',  'Cheque', 'Bank Transfer', 'Online', 'Other'],
      paymentErrors: { payment_method: '', remarks: '' },
    };
  },

  computed: {
    ...mapState("theme", ["isDark", "primaryColor", "buttonColor"]),
    brandColor() {
      return this.buttonColor || this.primaryColor || "#30384d";
    },

    today() {
      return moment().format('YYYY-MM-DD')
    },

    columns() {
      return [
        {
          key: "member_name",
          label: this.$t("houseBills.headers.member") || "Member",
          width: "240px",
        },
        {
          key: "flat_house_number",
          label: this.$t("houseBills.headers.flat_house_number") || "Unit",
          width: "140px",
        },
        {
          key: "title",
          label: this.$t("houseBills.headers.title") || "Bill Title",
          minWidth: "160px",
        },
        {
          key: "amount",
          label: this.$t("houseBills.headers.amount") || "Amount",
          width: "110px",
        },
        {
          key: "created_at",
          label: this.$t("houseBills.headers.date") || "Date",
          width: "140px",
        },
        {
          key: "is_paid",
          label: this.$t("houseBills.headers.status") || "Status",
          width: "130px",
        },
        {
          key: "paid_date",
          label: "Payment Info",
          width: "160px",
          align: "right",
        },
      ];
    },
  },

  watch: {
    date() {
      this.page = 1;
      this.getBills();
    },
    page() {
      this.getBills();
    },
    itemsPerPage() {
      this.page = 1;
      this.getBills();
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    onSearch(val) {
      this.search = val;
      this.sessionStorage(val);
      this.page = 1;
      this.getBills();
    },

    openDocument(item) {
      // item is a document_url string for bill payment receipts
      this.mediaItems_bill = [{
        url: this.base_url + '/uploads/' + item,
        type: 'image',
        name: item.split('/').pop(),
      }];
      this.mediaIndex_bill = 0;
      this.imageDialog = true;
    },

    downloadReceipt(receipt_path) {
      const url = this.base_url + '/uploads/' + receipt_path;
      // Download programmatically
      fetch(url)
        .then(response => response.blob())
        .then(blob => {
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.setAttribute("download", receipt_path.split('/').pop());
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(blobUrl);
        })
        .catch(err => {
          console.error("Failed to download receipt:", err);
          window.open(url, '_blank');
        });
    },

    updateBillStatus(item, value, ref_id = '', payment_method = '', paid_date = null, remarks = '') {
      // Show loader during status update API call
      this.loading = true;
      const formData = new FormData();
      formData.append("status", value);
      if (ref_id) formData.append("ref_id", ref_id);
      if (payment_method) formData.append("payment_method", payment_method);
      if (paid_date) formData.append("paid_date", paid_date);
      if (remarks) formData.append("remarks", remarks);


      const url = `societyadmin/bills/house/${item.id}/updateStatus`;
      this.$axios
        .post(url, formData)
        .then((response) => {
          const data = response.data;
          if (data.status) {
            this.setAlert({
              showAlert: true,
              message: data.message,
              variant: "green accent-4",
              timeout: 2000,
            });
            this.getBills();
          } else {
            this.handleErrors(data);
          }
        });
    },

    toggleStatusMenu(event, item) {
      if (this.statusMenu.open && this.statusMenu.item === item) {
        this.statusMenu.open = false;
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      this.statusMenu = {
        open: true,
        x: rect.left,
        y: rect.bottom + 4,
        item,
      };
    },

    selectStatus(item, value) {
      this.statusMenu.open = false;
      if (!item || item.is_paid == value) return;
      // if (value == 1) {
      //   // Open modal for payment details — default paid_date = today
      //   this.paymentErrors = { payment_method: '', remarks: '' };
      //   this.paymentModal = {
      //     open: true, item,
      //     ref_id: '', payment_method: '', remarks: '',
      //     paid_date: moment().format('YYYY-MM-DD'),
      //   };
      // } else {
      //   this.updateBillStatus(item, value, '', '');
      // }
        this.updateBillStatus(item, value, '', '');

    },

    submitPaymentModal() {
      const { item, ref_id, payment_method, paid_date, remarks } = this.paymentModal;

      // ── Validation ──────────────────────────────
      this.paymentErrors = { payment_method: '', remarks: '' };
      let hasError = false;
      if (!payment_method) {
        this.paymentErrors.payment_method = 'Payment method is required';
        hasError = true;
      }
      if (!remarks || !remarks.trim()) {
        this.paymentErrors.remarks = 'Remark is required';
        hasError = true;
      }
      if (hasError) return;

      // Combine selected date with current time → UTC ms timestamp
    const paid_date_utc = paid_date
      ? moment(paid_date).startOf('day').utc().valueOf()
      : moment().startOf('day').utc().valueOf();
      this.paymentModal.open = false;
      this.updateBillStatus(item, 1, ref_id, payment_method, paid_date_utc, remarks);
    },

    getBills() {
      this.loading = true;
      let month, year;
      if (this.date) {
        month = moment(this.date, "YYYY-MM").get("month") + 1;
        year = moment(this.date, "YYYY-MM").get("year");
      }

      let url = `societyadmin/bills/house/bills?society_id=${this.user.id}&perPage=${this.itemsPerPage}&page=${this.page}`;
      if (this.date) url += `&month=${month}&year=${year}`;
      if (this.search) url += `&search=${this.search}`;

      this.$axios
        .get(url)
        .then((response) => {
          this.loading = false;
          const data = response.data;
          if (data.status) {
            this.bills = data.housebills.data || data.housebills || [];
            this.totalRecords =
              data.total ||
              data.Total ||
              (data.housebills && data.housebills.total) ||
              0;

            // Update summary
            if (data.summary) {
              this.summary = {
                total_billed: data.summary.total_billed || 0,
                collected: data.summary.collected || 0,
                pending: data.summary.pending || 0,
                overdue: data.summary.overdue || 0,
              };
            } else {
              this.calculateStats();
            }
          } else {
            this.showError(data.message);
            if (data.type == "invalid") {
              localStorage.removeItem("user");
              this.$router.push("/societyadmin/login");
            }
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },

    calculateStats() {
      // Fallback: calculate stats from current page data
      const pending = this.bills.filter(
        (b) => b.is_paid == 0 || b.is_paid == "0",
      ).length;
      const totalAmount = this.bills.reduce(
        (sum, b) => sum + Number(b.amount || 0),
        0,
      );

      this.summary = {
        total_billed: totalAmount,
        collected: 0,
        pending: pending,
        overdue: 0,
      };
    },
    formatCurrency(val) {
      if (!val || val == 0) return "₹0";
      if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
      if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
      return `₹${Number(val).toLocaleString()}`;
    },
    exportToExcel() {
      let month, year;
      if (this.date) {
        month = moment(this.date, "YYYY-MM").get("month") + 1;
        year = moment(this.date, "YYYY-MM").get("year");
      }

      this.$axios
        .get("societyadmin/export/bills", {
          params: {
            society_id: this.user.id,
            month,
            year,
            search: this.search,
          },
          responseType: "blob",
        })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `House_Bills_${moment().format("YYYY-MM-DD")}.xlsx`,
          );
          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch(() => {
          this.showError("Failed to export data");
        });
    },

    getFormattedDate(date) {
      return date ? moment(date).format("DD MMM, YYYY · hh:mm A") : "N/A";
    },

    sessionStorage(search) {
      if (!search) sessionStorage.removeItem("bill_search");
      else sessionStorage.setItem("bill_search", search);
    },

    handleErrors(data) {
      if (data.errors) {
        this.showError(data.errors[0].message || data.message);
      } else {
        this.showError(data.message);
      }
    },

    showError(msg) {
      this.setAlert({
        showAlert: true,
        variant: "red accent-3",
        timeout: "2000",
        message: msg,
      });
    },
  },

  mounted() {
    this.user = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : {};
    this.search = sessionStorage.getItem("bill_search") || "";
    // Setting date triggers the watch, which calls getBills()
    this.date = moment().format("YYYY-MM");

    // Close status dropdowns when clicking outside
    document.addEventListener("click", () => {
      this.statusMenu.open = false;
    });
  },
};
</script>

<style scoped>
.status-pill-wrap {
  position: relative;
  display: inline-block;
}
.bill-list-container {
  padding: 24px;
}

.page-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

@media (max-width: 1200px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 600px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
}

/* ── Stats Row ── */
.stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  margin-bottom: 4px;
}

@media (max-width: 768px) {
  .stats-row {
    grid-template-columns: 1fr;
  }
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

@media (max-width: 600px) {
  .bill-list-container {
    padding: 12px;
  }
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  .header-controls {
    width: 100%;
  }
  .header-controls >>> .premium-date-wrap {
    flex: 1;
  }
}

.custom-date-trigger {
  display: flex;
  align-items: center;
  height: 38px;
  padding: 0 12px;
  background: #ffffff;
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--tx2, #374151);
  cursor: pointer;
  transition: all 0.2s;
  min-width: 140px;
}
.custom-date-trigger:hover {
  border-color: var(--brand, #0d9972);
  background: var(--surface2, #f8fafc);
}

.icon-action-btn {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  border: 1px solid var(--border, #e2e8f0);
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--tx2, #374151);
  cursor: pointer;
  transition: all 0.2s;
}
.icon-action-btn:hover {
  border-color: var(--brand, #0d9972);
  color: var(--brand, #0d9972);
  background: var(--surface2, #f8fafc);
}

/* ── Cells ── */
.member-cell {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.member-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--tx, #111827);
}
.house-number-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px;
  border-radius: 8px;
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  font-size: 12px;
  color: var(--tx, #111827);
  width: fit-content;
}
.house-number-badge .flat-prefix {
  color: var(--tx3, #6b7280);
}
.house-number-badge .house-num {
  color: var(--tx3, #6b7280);
}

.bill-title-text {
  font-size: 12.5px;
  font-weight: 500;
  color: var(--tx2, #374151);
}
.amount-text {
  font-weight: 700;
  color: var(--tx, #111827);
  font-family: "JetBrains Mono", monospace;
  font-size: 13px;
}
.date-text {
  font-size: 12.5px;
  color: var(--tx3, #6b7280);
}

/* Status Pill */
.status-pill-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px 3px 7px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
}
.status-pending {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}
.status-pending .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #f59e0b;
  flex-shrink: 0;
}
.status-completed {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}
.status-completed .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #10b981;
  flex-shrink: 0;
}
.status-pill-btn:hover {
  transform: translateY(-1px);
  filter: brightness(1.05);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
}

/* Paid Date & Doc */
.paid-date-cell {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}
.doc-view-btn {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--surface2, #f8fafc);
  color: var(--brand, #0d9972);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border, #e2e8f0);
  cursor: pointer;
  transition: all 0.2s;
}
.doc-view-btn:hover {
  background: var(--brand, #0d9972);
  color: #fff;
  border-color: var(--brand, #0d9972);
}


.info-item label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--tx4, #9ca3af);
  letter-spacing: 0.05em;
}
.info-item span {
  font-size: 13px;
  font-weight: 600;
  color: var(--tx2, #374151);
}
</style>

<style>
/* Fixed-position status dropdown portal — outside scoped so it renders above everything */
.status-dropdown-portal {
  position: fixed;
  z-index: 9999;
  background: var(--surface, #ffffff);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.14);
  min-width: 155px;
  overflow: hidden;
  animation: sdpFadeIn 0.15s ease;
}

@keyframes sdpFadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.status-dropdown-portal .status-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--tx2, #374151);
  cursor: pointer;
  transition: background 0.12s;
}
.status-dropdown-portal .status-option:hover {
  background: var(--surface2, #f8fafc);
}
.status-dropdown-portal .status-option.active {
  background: var(--surface2, #f8fafc);
  color: var(--tx, #111827);
}
.status-dropdown-portal .status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}
.cm2-dark .info-item span {
  color: #cbd5e1;
}
.info-item .amount {
  color: var(--brand, #0d9972) !important;
  font-family: "JetBrains Mono", monospace;
  font-size: 14px;
}

.cm2-field-select {
  margin-bottom: 4px;
}
.cm2-footer {
  display: flex;
  gap: 10px;
  padding: 14px 22px 20px;
  justify-content: flex-end;
}
.cm2-btn-cancel {
  padding: 9px 18px;
  border-radius: 10px;
  border: 1px solid var(--border, #e2e8f0);
  background: transparent;
  color: var(--tx2, #374151);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: "Instrument Sans", sans-serif;
}
.cm2-btn-cancel:hover {
  background: var(--surface2, #f8fafc);
}
.cm2-btn-save {
  padding: 9px 20px;
  border-radius: 10px;
  border: none;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-family: "Instrument Sans", sans-serif;
}
.cm2-btn-save:hover {
  filter: brightness(1.1);
  transform: translateY(-1px);
}

@keyframes cm2FadeIn {
  from {
    opacity: 0;
    transform: scale(0.96) translateY(8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Export Button */
.export-btn {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.export-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  filter: brightness(1.1);
}

.export-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}
</style>
